import axios from 'axios'
import auth from '@src/auth'
import get from 'lodash/get'
import canUserRead from '@utils/can-user-read'

export const state = {
  currentUser: getSavedState('auth.currentUser'),
}

export const mutations = {
  SET_CURRENT_USER(state, newValue) {
    state.currentUser = newValue
    saveState('auth.currentUser', newValue)
    setDefaultAuthHeaders(state)
  },
}

export const getters = {
  // Whether the user is currently logged in.
  loggedIn(state) {
    return !!state.currentUser
  },
  currentUserAuthorization(state) {
    return get(
      state,
      [
        'currentUser',
        'https://link.communityhousingnetwork.org/app_metadata',
        'authorization',
      ],
      {}
    )
  },
  currentUserGroups(state, getters) {
    return getters.currentUserAuthorization.groups || []
  },
  currentUserRoles(state, getters) {
    return getters.currentUserAuthorization.roles || []
  },
  currentUserPermissions(state, getters) {
    return getters.currentUserAuthorization.permissions || []
  },
  canCurrentUserRead(state, getters) {
    return (tableName, fieldName) =>
      canUserRead(tableName, fieldName, getters.currentUserPermissions)
  },
}

export const actions = {
  // This is automatically run in `src/state/store.js` when the app
  // starts, along with any other actions named `init` in other modules.
  init({ state, dispatch }) {
    setDefaultAuthHeaders(state)
  },
  logIn({ commit }, { routeTo, authOptions = {} } = {}) {
    getAnalytics().event({
      eventCategory: 'Authentication',
      eventAction: 'Initiate login',
      eventLabel: `From ${routeTo.fullPath}`,
    })
    if (routeTo) {
      window.localStorage.setItem('redirectTo', routeTo.fullPath)
    }
    auth.authorize({
      redirectUri: window.location.origin + '/callback',
      ADDITIONAL_PARAMETERS: JSON.stringify(authOptions),
    })
  },
  // Logs out the current user.
  logOut({ state, commit }, { routeFrom } = {}) {
    const previousUser = state.currentUser
    commit('SET_CURRENT_USER', null)
    getAnalytics().event({
      eventCategory: 'Authentication',
      eventAction: 'Successful logout',
      eventLabel: previousUser.email,
    })
    if (routeFrom) {
      window.localStorage.setItem('redirectTo', routeFrom.fullPath)
    }
    auth.logout({ returnTo: window.location.origin + '/callback' })
  },
  // Validates the current user's token and refreshes it
  // with new data from the API.
  validate({ commit, state }) {
    return new Promise((resolve, reject) => {
      if (window.location.hash) {
        auth.parseHash({ hash: window.location.hash }, (error, authResult) => {
          if (error) {
            reject(error)
          } else {
            commit('SET_CURRENT_USER', {
              accessToken: authResult.accessToken,
              idToken: authResult.idToken,
              ...authResult.idTokenPayload,
            })
            resolve(state.currentUser)
          }
        })
      } else {
        validateCurrentUser()
      }

      function validateCurrentUser() {
        if (userTokenExpired(state.currentUser)) {
          commit('SET_CURRENT_USER', null)
        }

        resolve(state.currentUser)
      }
    })
  },
}

// ===
// Private helpers
// ===

function getSavedState(key) {
  return JSON.parse(window.localStorage.getItem(key))
}

function saveState(key, state) {
  window.localStorage.setItem(key, JSON.stringify(state))
}

function setDefaultAuthHeaders(state) {
  axios.defaults.headers.common.Authorization = state.currentUser
    ? `Bearer ${state.currentUser.idToken}`
    : ''
}

function userTokenExpired(user) {
  return !user || !user.accessToken || user.exp < Date.now() / 1000
}

function getAnalytics() {
  return require('vue-analytics')
}
